import React, { useState, useRef, useEffect } from 'react';
import '../styles/DatePickerr.css';
import "../assets/css/Style.css";
import calander from "../assets/images/icons/calendar-billing.svg";

const CustomDatePicker = ({onChange ,dueDate}) => {
    const today = new Date();
    const handleDateSelect = (e) => {
        onChange(e);
     };
    const [view, setView] = useState('day');
    const [selectedDate, setSelectedDate] = useState(today);
    const [currentMonth, setCurrentMonth] = useState(today.getMonth());
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [showCalendar, setShowCalendar] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);

    const calendarRef = useRef(null);
    const inputRef = useRef(null);
    const iconRef = useRef(null);
    const prevRef = useRef(null);
    const minDate = new Date();
    const maxDate = dueDate? new Date(dueDate??dueDate) : new Date();	
    maxDate.setDate(dueDate?maxDate.getDate():maxDate.getDate()+30);
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const dayNamesShort = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    
    const getDaysInMonth = (month, year) => {
        return new Date(year, month + 1, 0).getDate();
    };
    
    const formatDate = (date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    };
    
    const isDateDisabled = (day, month, year) => {
        const date = new Date(year, month, day);
        return date < minDate || date > maxDate;
    };
    
    const handleDayClick = (day) => {
        const newDate = new Date(currentYear, currentMonth, day);
        if (!isDateDisabled(day, currentMonth, currentYear)) {
            prevRef.current = selectedDate;
            setSelectedDate(newDate);
            setShowCalendar(false);
            setFocusedIndex(-1);
            handleDateSelect(formatDate(newDate));
        }
    };
    
    const handleMonthClick = (monthIndex) => {
        const minMonth = minDate.getFullYear() === currentYear ? minDate.getMonth() : 0;
        const maxMonth = maxDate.getFullYear() === currentYear ? maxDate.getMonth() : 11;
        if (monthIndex >= minMonth && monthIndex <= maxMonth) {
            setCurrentMonth(monthIndex);
            setView('day');
            setFocusedIndex(-1);
        }
    };

    const handleYearClick = (year) => {
        if (year >= minDate.getFullYear() && year <= maxDate.getFullYear()) {
            setCurrentYear(year);
            setView('month');
            setFocusedIndex(-1);
        }
    };

    const handlePrev = () => {
        if (view === 'day') {
            const newMonth = currentMonth === 0 ? 11 : currentMonth - 1;
            const newYear = currentMonth === 0 ? currentYear - 1 : currentYear;
            if (new Date(newYear, newMonth, 1) >= new Date(minDate.getFullYear(), minDate.getMonth(), 1)) {
                setCurrentMonth(newMonth);
                setCurrentYear(newYear);
                setFocusedIndex(-1);
            }
        } else if (view === 'month') {
            const newYear = currentYear - 1;
            if (newYear >= minDate.getFullYear()) {
                setCurrentYear(newYear);
                setFocusedIndex(-1);
            }
        } else {
            const newYear = currentYear - 12;
            if (newYear >= minDate.getFullYear()) {
                setCurrentYear(newYear);
                setFocusedIndex(-1);
            }
        }
    };

    const handleNext = () => {
        if (view === 'day') {
            const newMonth = currentMonth === 11 ? 0 : currentMonth + 1;
            const newYear = currentMonth === 11 ? currentYear + 1 : currentYear;

            if (new Date(newYear, newMonth, 1) <= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)) {
                setCurrentMonth(newMonth);
                setCurrentYear(newYear);
                setFocusedIndex(-1);
            }            
        } else if (view === 'month') {
            const newYear = currentYear + 1;
            if (newYear <= maxDate.getFullYear()) {
                setCurrentYear(newYear);
                setFocusedIndex(-1);
            }
        } else {
            const newYear = currentYear + 12;
            if (newYear <= maxDate.getFullYear()) {
                setCurrentYear(newYear);
                setFocusedIndex(-1);
            }
        }
    };

    // Keyboard navigation handlers
    const handleKeyDown = (e) => {
        if (!showCalendar) return;

        switch (e.key) {
            case 'Escape':
                setShowCalendar(false);
                setFocusedIndex(-1);
                inputRef.current?.focus();
                break;
                
            case 'Tab':
                if (!e.shiftKey && focusedIndex === -1) {
                    setShowCalendar(false);
                }
                break;
                
            case 'ArrowLeft':
                e.preventDefault();
                navigateCalendar('left');
                break;
                
            case 'ArrowRight':
                e.preventDefault();
                navigateCalendar('right');
                break;
                
            case 'ArrowUp':
                e.preventDefault();
                navigateCalendar('up');
                break;
                
            case 'ArrowDown':
                e.preventDefault();
                navigateCalendar('down');
                break;
                
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (focusedIndex >= 0) {
                    handleFocusedItemSelect();
                }
                break;
                
            case 'Home':
                e.preventDefault();
                focusFirstItem();
                break;
                
            case 'End':
                e.preventDefault();
                focusLastItem();
                break;
                
            default:
                break;
        }
    };

    const navigateCalendar = (direction) => {
        if (view === 'day') {
            navigateDays(direction);
        } else if (view === 'month') {
            navigateMonths(direction);
        } else if (view === 'year') {
            navigateYears(direction);
        }
    };

    const navigateDays = (direction) => {
        const daysInMonth = getDaysInMonth(currentMonth, currentYear);
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
        const totalCells = 42;
        let newIndex = focusedIndex;

        switch (direction) {
            case 'left':
                newIndex = Math.max(0, focusedIndex - 1);
                break;
            case 'right':
                newIndex = Math.min(totalCells - 1, focusedIndex + 1);
                break;
            case 'up':
                newIndex = Math.max(0, focusedIndex - 7);
                break;
            case 'down':
                newIndex = Math.min(totalCells - 1, focusedIndex + 7);
                break;
        }

        if (newIndex >= 0 && newIndex < totalCells) {
            const dayIndex = newIndex - firstDayOfMonth;
            if (dayIndex >= 0 && dayIndex < daysInMonth) {
                const day = dayIndex + 1;
                if (!isDateDisabled(day, currentMonth, currentYear)) {
                    setFocusedIndex(newIndex);
                }
            } else {
                setFocusedIndex(newIndex);
            }
        }
    };

    const navigateMonths = (direction) => {
        const totalMonths = 12;
        let newIndex = focusedIndex;

        switch (direction) {
            case 'left':
                newIndex = Math.max(0, focusedIndex - 1);
                break;
            case 'right':
                newIndex = Math.min(totalMonths - 1, focusedIndex + 1);
                break;
            case 'up':
                newIndex = Math.max(0, focusedIndex - 4);
                break;
            case 'down':
                newIndex = Math.min(totalMonths - 1, focusedIndex + 4);
                break;
        }

        const monthIndex = newIndex;
        const isDisabled = 
            (currentYear === minDate.getFullYear() && monthIndex < minDate.getMonth()) ||
            (currentYear === maxDate.getFullYear() && monthIndex > maxDate.getMonth());

        if (!isDisabled) {
            setFocusedIndex(newIndex);
        }
    };

    const navigateYears = (direction) => {
        const totalYears = 12;
        let newIndex = focusedIndex;

        switch (direction) {
            case 'left':
                newIndex = Math.max(0, focusedIndex - 1);
                break;
            case 'right':
                newIndex = Math.min(totalYears - 1, focusedIndex + 1);
                break;
            case 'up':
                newIndex = Math.max(0, focusedIndex - 4);
                break;
            case 'down':
                newIndex = Math.min(totalYears - 1, focusedIndex + 4);
                break;
        }

        const startYear = currentYear - (currentYear % 10);
        const year = startYear + newIndex - 1;
        const isDisabled = year < minDate.getFullYear() || year > maxDate.getFullYear();

        if (!isDisabled) {
            setFocusedIndex(newIndex);
        }
    };

    const handleFocusedItemSelect = () => {
        if (view === 'day') {
            const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
            const dayIndex = focusedIndex - firstDayOfMonth;
            if (dayIndex >= 0) {
                const day = dayIndex + 1;
                const daysInMonth = getDaysInMonth(currentMonth, currentYear);
                if (day <= daysInMonth && !isDateDisabled(day, currentMonth, currentYear)) {
                    handleDayClick(day);
                }
            }
        } else if (view === 'month') {
            handleMonthClick(focusedIndex);
        } else if (view === 'year') {
            const startYear = currentYear - (currentYear % 10);
            const year = startYear + focusedIndex - 1;
            handleYearClick(year);
        }
    };

    const focusFirstItem = () => {
        setFocusedIndex(0);
    };

    const focusLastItem = () => {
        if (view === 'day') {
            setFocusedIndex(41);
        } else if (view === 'month') {
            setFocusedIndex(11); 
        } else if (view === 'year') {
            setFocusedIndex(11); 
        }
    };

    const getDayTabIndex = (dayIndex, day, isDisabled) => {
        if (focusedIndex === dayIndex && !isDisabled) {
            return 0;
        }
        return -1;
    };

    const getMonthTabIndex = (monthIndex, isDisabled) => {
        if (focusedIndex === monthIndex && !isDisabled) {
            return 0;
        }
        return -1;
    };

    const getYearTabIndex = (yearIndex, isDisabled) => {
        if (focusedIndex === yearIndex && !isDisabled) {
            return 0;
        }
        return -1;
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                calendarRef.current &&
                !calendarRef.current.contains(e.target) &&
                !inputRef.current.contains(e.target)
                && !iconRef.current.contains(e.target)
            ) {
                setShowCalendar(false);
                setFocusedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (showCalendar) {
            const todayCellIndex = new Date(currentYear, currentMonth, today.getDate());
            const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
            const todayIndex = today.getDate() - 1 + firstDayOfMonth;
            
            if (today.getMonth() === currentMonth && today.getFullYear() === currentYear) {
                setFocusedIndex(todayIndex);
            } else {
                setFocusedIndex(-1);
            }
        }
    }, [showCalendar, currentMonth, currentYear]);

    const renderDayView = () => {
        const daysInMonth = getDaysInMonth(currentMonth, currentYear);
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
        const days = [];

        const daysInPrevMonth = getDaysInMonth(currentMonth - 1, currentYear);
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(
                <td key={`prev-${i}`} className="day old disabled">
                    {daysInPrevMonth - firstDayOfMonth + i + 1}
                </td>
            );
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const isSelected = selectedDate.getDate() === day &&
                selectedDate.getMonth() === currentMonth &&
                selectedDate.getFullYear() === currentYear;
            const isToday = today.getDate() === day &&
                today.getMonth() === currentMonth &&
                today.getFullYear() === currentYear;
            const isDisabled = isDateDisabled(day, currentMonth, currentYear);
            const dayIndex = firstDayOfMonth + day - 1;

            let className = 'day';
            if (isSelected) className += ' active';
            if (isToday) className += ' today';
            if (isDisabled) className += ' disabled';
            if (focusedIndex === dayIndex && !isDisabled) className += ' focused';

            days.push(
                <td
                    key={`curr-${day}`}
                    className={className}
                    onClick={() => !isDisabled && handleDayClick(day)}
                    tabIndex={getDayTabIndex(dayIndex, day, isDisabled)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleDayClick(day);
                        }
                    }}
                    aria-label={`${day} ${monthNames[currentMonth]} ${currentYear}${isSelected ? ', selected' : ''}${isToday ? ', today' : ''}`}
                    role="button"
                    aria-disabled={isDisabled}
                >
                    {day}
                </td>
            );
        }

        const daysToAdd = 42 - days.length;
        for (let i = 1; i <= daysToAdd; i++) {
            days.push(
                <td key={`next-${i}`} className="day new disabled">
                    {i}
                </td>
            );
        }

        const weeks = [];
        for (let i = 0; i < days.length; i += 7) {
            weeks.push(<tr key={`week-${i}`}>{days.slice(i, i + 7)}</tr>);
        }

        return (
            <div className="datepicker-days">
                <table className="table-condensed" role="grid" aria-label="Calendar">
                    <thead>
                        <tr>
                            <th 
                                className={`prev ${new Date(currentYear, currentMonth - 1, 1) < new Date(minDate.getFullYear(), minDate.getMonth(), 1) ? 'disabled' : ''}`}
                                onClick={new Date(currentYear, currentMonth - 1, 1) >= new Date(minDate.getFullYear(), minDate.getMonth(), 1) ? handlePrev : undefined}
                                tabIndex={new Date(currentYear, currentMonth - 1, 1) >= new Date(minDate.getFullYear(), minDate.getMonth(), 1) ? 0 : -1}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handlePrev();
                                    }
                                }}
                                role="button"
                                aria-label="Previous month"
                            >
                                &laquo;
                            </th>
                            <th 
                                colSpan="5" 
                                className="datepicker-switch" 
                                onClick={() => setView('month')}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setView('month');
                                        setFocusedIndex(currentMonth);
                                    }
                                }}
                                role="button"
                                aria-label={`Change month, current view: ${monthNames[currentMonth]} ${currentYear}`}
                            >
                                {monthNames[currentMonth]} {currentYear}
                            </th>
                            <th 
                                className={`next ${new Date(currentYear, currentMonth + 1, 1) > new Date(maxDate.getFullYear(), maxDate.getMonth(), 1) ? 'disabled' : ''}`}
                                onClick={new Date(currentYear, currentMonth + 1, 1) <= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1) ? handleNext : undefined}
                                tabIndex={new Date(currentYear, currentMonth + 1, 1) <= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1) ? 0 : -1}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleNext();
                                    }
                                }}
                                role="button"
                                aria-label="Next month"
                            >
                                &raquo;
                            </th>
                        </tr>
                        <tr>
                            {dayNamesShort.map(day => (
                                <th key={day} className="dow" scope="col">{day}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>{weeks}</tbody>
                </table>
            </div>
        );
    };

    const renderMonthView = () => {
        const months = monthNames.map((month, index) => {
            const isSelected = index === currentMonth;
            const isCurrent = today.getMonth() === index && today.getFullYear() === currentYear;
            const isDisabled =
                (currentYear === minDate.getFullYear() && index < minDate.getMonth()) ||
                (currentYear === maxDate.getFullYear() && index > maxDate.getMonth());

            let className = 'month';
            if (isSelected) className += ' active';
            if (isCurrent) className += ' today';
            if (isDisabled) className += ' disabled';
            if (focusedIndex === index && !isDisabled) className += ' focused';

            return (
                <span
                    key={month}
                    className={className}
                    onClick={() => !isDisabled && handleMonthClick(index)}
                    tabIndex={getMonthTabIndex(index, isDisabled)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleMonthClick(index);
                        }
                    }}
                    role="button"
                    aria-label={`${month} ${currentYear}${isSelected ? ', selected' : ''}`}
                    aria-disabled={isDisabled}
                >
                    {month.substring(0, 3)}
                </span>
            );
        });

        return (
            <div className="datepicker-months">
                <table className="table-condensed" role="grid" aria-label="Month selection">
                    <thead>
                        <tr>
                            <th 
                                className={`prev ${currentYear <= minDate.getFullYear() ? 'disabled' : ''}`}
                                onClick={currentYear > minDate.getFullYear() ? handlePrev : undefined}
                                tabIndex={currentYear > minDate.getFullYear() ? 0 : -1}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handlePrev();
                                    }
                                }}
                                role="button"
                                aria-label="Previous year"
                            >
                                &laquo;
                            </th>
                            <th 
                                colSpan="5" 
                                className="datepicker-switch" 
                                onClick={() => setView('year')}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setView('year');
                                        setFocusedIndex(1);
                                    }
                                }}
                                role="button"
                                aria-label={`Change year, current year: ${currentYear}`}
                            >
                                {currentYear}
                            </th>
                            <th 
                                className={`next ${currentYear >= maxDate.getFullYear() ? 'disabled' : ''}`}
                                onClick={currentYear < maxDate.getFullYear() ? handleNext : undefined}
                                tabIndex={currentYear < maxDate.getFullYear() ? 0 : -1}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleNext();
                                    }
                                }}
                                role="button"
                                aria-label="Next year"
                            >
                                &raquo;
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr><td colSpan="7">{months.slice(0, 4)}</td></tr>
                        <tr><td colSpan="7">{months.slice(4, 8)}</td></tr>
                        <tr><td colSpan="7">{months.slice(8, 12)}</td></tr>
                    </tbody>
                </table>
            </div>
        );
    };

    const renderYearView = () => {
        const startYear = currentYear - (currentYear % 10);
        const years = Array.from({ length: 12 }, (_, i) => startYear + i - 1);
        const yearCells = years.map((year, index) => {
            const isSelected = year === currentYear;
            const isCurrent = today.getFullYear() === year;
            const isDisabled = year < minDate.getFullYear() || year > maxDate.getFullYear();

            let className = 'year';
            if (isSelected) className += ' active';
            if (isCurrent) className += ' today';
            if (isDisabled) className += ' disabled';
            if (focusedIndex === index && !isDisabled) className += ' focused';

            return (
                <span
                    key={year}
                    className={className}
                    onClick={() => !isDisabled && handleYearClick(year)}
                    tabIndex={getYearTabIndex(index, isDisabled)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleYearClick(year);
                        }
                    }}
                    role="button"
                    aria-label={`Year ${year}${isSelected ? ', selected' : ''}`}
                    aria-disabled={isDisabled}
                >
                    {year}
                </span>
            );
        });

        return (
            <div className="datepicker-years">
                <table className="table-condensed" role="grid" aria-label="Year selection">
                    <thead>
                        <tr>
                            <th 
                                className={`prev ${startYear - 1 < minDate.getFullYear() ? 'disabled' : ''}`}
                                onClick={startYear - 1 >= minDate.getFullYear() ? handlePrev : undefined}
                                tabIndex={startYear - 1 >= minDate.getFullYear() ? 0 : -1}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handlePrev();
                                    }
                                }}
                                role="button"
                                aria-label="Previous decade"
                            >
                                &laquo;
                            </th>
                            <th 
                                colSpan="5" 
                                className="datepicker-switch" 
                                onClick={() => setView('day')}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setView('day');
                                        setFocusedIndex(-1);
                                    }
                                }}
                                role="button"
                                aria-label={`Back to day view, current range: ${startYear - 1} to ${startYear + 10}`}
                            >
                                {startYear - 1}-{startYear + 10}
                            </th>
                            <th 
                                className={`next ${startYear + 11 > maxDate.getFullYear() ? 'disabled' : ''}`}
                                onClick={startYear + 11 <= maxDate.getFullYear() ? handleNext : undefined}
                                tabIndex={startYear + 11 <= maxDate.getFullYear() ? 0 : -1}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleNext();
                                    }
                                }}
                                role="button"
                                aria-label="Next decade"
                            >
                                &raquo;
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr><td colSpan="7">{yearCells.slice(0, 4)}</td></tr>
                        <tr><td colSpan="7">{yearCells.slice(4, 8)}</td></tr>
                        <tr><td colSpan="7">{yearCells.slice(8, 12)}</td></tr>
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="mb-3 radio datepicks position-relative">
            <input
                ref={inputRef}
                id="datepicker-input"
                type="text"
                className="form-control2 schedulepaymentdate form-width schedulepaymentbox"
                value={selectedDate ? formatDate(selectedDate) : ""}
                onClick={() => {
                    setShowCalendar((prev) => !prev);
                    setFocusedIndex(-1);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowCalendar((prev) => !prev);
                        setFocusedIndex(-1);
                    }
                }}
                readOnly
                aria-label="Select date"
                aria-haspopup="dialog"
                aria-expanded={showCalendar}
                required
                placeholder='MM/DD/YYYY'
            />
            <img
                src={calander}
                className="asset position-absolute calender-icon"
                alt="Calendar"
                onClick={() => {
                    setShowCalendar((prev) => !prev);
                    setFocusedIndex(-1);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowCalendar((prev) => !prev);
                        setFocusedIndex(-1);
                    }
                }}
                ref={iconRef}
                style={{ cursor: 'pointer' }}
                tabIndex={0}
                role="button"
                aria-label="Open calendar"
            />

            {showCalendar && (
                <div 
                    className="datepicker1" 
                    ref={calendarRef}
                    onKeyDown={handleKeyDown}
                    tabIndex={-1}
                    role="dialog"
                    aria-label="Date picker"
                    aria-modal="true"
                >
                    {view === 'day' && renderDayView()}
                    {view === 'month' && renderMonthView()}
                    {view === 'year' && renderYearView()}
                </div>
            )}
        </div>
    );
};

export default CustomDatePicker;